import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import { DragIndicator, MoreHoriz } from '@material-ui/icons';
import DuplicatePopupModel from '../../../../../shared/duplicate-popup/Duplicate-Popup';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import { useDispatch, useSelector } from 'react-redux';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import { GUEST_EDIT, GUEST_VIEW } from '../../../../../constants/EmailServicesConst';
import {
  RBAC_PERMISSION_LEVEL_FULL_ACCESS,
  RBAC_TYPE_EMAIL_SERVICES,
} from '../../../../../constants/RoleConsts';
import SearchBar from '../../../../../shared/search-bar/Search-Bar';
import TablePaginationActions from '../../../../../shared/table-pagination/TablePaginationActions';
import DeletePopupModel from '../../../../../shared/delete-popup/Delete-Popup';
import CustomPopupModel from '../../../../../shared/custom-popup/Custom-Popup';
import WarningPopup from '../../../../../shared/warning-popup/Warning-Popup';
import LinearProgressBar from '../../../../../shared/progress-bar/Progress';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import useOutsideAlerter from '../../../../../custom-hooks/useOutsideAlerter';
import { handleOnDragEnd } from '../../../../../shared/custom-dragdrop/dragDrop';
import { danger, success } from '../../../../../utils/formUtils';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import { decryptLocalStorage } from '../../../../../utils/authUtils';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../../utils/loaderSlice';
import { createDataSortMapping } from '../../../../../utils/PropertyAPIUtils';
import {
  emailServicesSelector,
  fetchAllEmailServices,
  setEmailServices,
} from '../../../../services/servicesSlice';
import {
  createEmailServiceTemplate,
  deleteEmailTemplate,
  publishEmailTemplate,
  unPublishEmailTemplate,
} from '../../../../../utils/cmsUtils';
import './GuestList.scss';
import VisibilityIcon from '@mui/icons-material/Visibility';

const GUEST_FILTER = [
  {
    id: 'status',
    title: 'Status',
    field: 'status',
    subMenu: [
      { id: 'Published', name: 'Published', selected: false },
      { id: 'Draft', name: 'Draft', selected: false },
    ],
  },
];

const GuestList = (props) => {
  const {
    onDrawerToggle,
    onViewToggle,
    selectedHotel,
    permissions,
    page,
    rowsPerPage,
    onsetRowsPerPage,
    onsetPage,
  } = props;

  const dispatch = useDispatch();

  const [guestList, setGuestList] = useState([]);
  const [originalGuestList, setOriginalGuestList] = useState([]);

  const [searchText, setSearchText] = useState('');
  const [filterDataList, setFilterDataList] = useState(GUEST_FILTER);
  const [loading, setLoading] = useState(true);

  const [visible, setVisible] = useState(false);
  const [popUp, setPopUp] = useState(false);

  const [dragEnabled, setDragEnabled] = useState(true);

  const [selectedValue, setSelectedValue] = useState({
    id: '',
    name: '',
    title: '',
    description: '',
    status: false,
    anchorEl: null,
    group: null,
    // single publish flow
    publishConfirm: false,
    publishButtonLabel: '',
    showTemplateWarning: false,
    duplicateStatus: false,
    showDeleteWarning: false,
  });

  const wrapperRef = useRef(null);
  const isFilterApplied = useRef(false);
  const prevHotelIdRef = useRef(selectedHotel);

  const emailServicesList = useSelector(emailServicesSelector);

  const lang = useMemo(() => {
    const l = decryptLocalStorage('lang');
    return l !== 'en' ? l : '';
  }, []);

  // ---- Helpers ----
  const resetFilterDataList = () => {
    setFilterDataList(
      GUEST_FILTER.map((filter) => ({
        ...filter,
        subMenu: filter.subMenu.map((s) => ({ ...s, selected: false })),
      })),
    );
    isFilterApplied.current = false;
  };

  const deviceFilterCount = 0;
  const audienceFilterCount = 0;
  const statusFilterCount =
    filterDataList.find((f) => f.field === 'status')?.subMenu?.filter((s) => s.selected)?.length ||
    0;

  const dragDisabled = !dragEnabled || guestList?.length <= 1;

  const getAudienceLabel = (row) => {
    if (row.audience?.type === 'GUESTS') return 'Guests';
    if (row.audience?.type === 'STAFF') return 'Staff';
    if (row.audience?.staff?.emails?.length > 0) return 'Staff';
    if (row.audience?.guests) return 'Guests';
    if (row.isForStaff) return 'Staff';
    if (row.isForGuest || row.isForGuests) return 'Guests';
    return '—';
  };

  const getTriggerLabel = (row) => {
    if (row.triggerActionLabel) return row.triggerActionLabel;
    if (row.triggerAction) return row.triggerAction;
    const t = (row.triggerType || row.trigger || '').toString().toUpperCase();
    if (t === 'INSTANT' || t === 'INSTANT_PUBLISH' || row.instantPublish || row.isInstant) {
      return 'Instant Publish';
    }
    if (t || row.isTrigger) return 'Trigger';
    return '—';
  };

  useOutsideAlerter(wrapperRef, visible, setPopUp);

  useEffect(() => {
    if (rowsPerPage !== 10) {
      onsetRowsPerPage(10);
      onsetPage(0);
    }
  }, []);

  useEffect(() => {
    if (prevHotelIdRef.current !== selectedHotel) {
      onsetPage(0);
    }
    prevHotelIdRef.current = selectedHotel;
  }, [selectedHotel, onsetPage]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setSearchText('');
      onsetPage(0);

      if (!selectedHotel) {
        setGuestList([]);
        setOriginalGuestList([]);
        props.setGlobalPlaceHolder?.([]);
        setLoading(false);
        return;
      }

      try {
        dispatch(stepUpLoaderCount());
        await dispatch(fetchAllEmailServices(selectedHotel));
      } catch (e) {
      } finally {
        dispatch(stepDownLoaderCount());
      }
    };

    resetFilterDataList();
    load();
  }, [selectedHotel, lang]);

  useEffect(() => {
    const mapped =
      (emailServicesList || []).map((item) => ({
        ...item,
        hotelId: selectedHotel,
        status: item.isPublished ? 'Published' : 'Draft',
        statusClass: item.isPublished ? 'pill-active' : 'pill-inactive theme-dark-green',
      })) ?? [];

    setGuestList(mapped);
    setOriginalGuestList(mapped);
    props.setGlobalPlaceHolder?.(mapped);
    setLoading(false);
    setSearchText('');
  }, [emailServicesList, props, selectedHotel]);

  const handleChangePage = (_, newPage) => {
    onsetPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    onsetRowsPerPage(parseInt(e.target.value, 10));
    onsetPage(0);
  };

  const handleCloseMenu = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      anchorEl: null,
      group: null,
      publishConfirm: false,
      publishButtonLabel: '',
      showTemplateWarning: false,
      duplicateStatus: false,
      showDeleteWarning: false,
    });
  };

  const onOpenDrawer = (event) => {
    event.stopPropagation();
    onDrawerToggle(GUEST_EDIT, true);
    handleCloseMenu();
  };

  const onViewDetails = (row, e) => {
    openViewDrawer(row, e);
  };

  const openViewDrawer = (row, e) => {
    e?.stopPropagation?.();
    const viewObj = { ...row, _viewMode: true }; // flag to tell drawer to open in view mode
    onDrawerToggle(GUEST_EDIT, true, viewObj);
    handleCloseMenu();
  };

  const onViewDetailsIcon = (e) => {
    if (
      e.target.className ===
      'MuiButtonBase-root MuiListItem-root MuiMenuItem-root pe-4 MuiMenuItem-gutters MuiListItem-gutters MuiListItem-button'
    ) {
      onViewToggle(GUEST_VIEW, true, selectedValue.group, 'view');
      handleCloseMenu();
    }
  };

  const handleCellMenuClick = (event, row) => {
    event.stopPropagation();
    setSelectedValue({
      id: row.id,
      name: row.name,
      title: '',
      description: '',
      status: false,
      anchorEl: event.currentTarget,
      group: row,
      publishConfirm: false,
      publishButtonLabel: '',
      showTemplateWarning: false,
    });
  };

  const handleSearchText = (text) => {
    setSearchText(text);
    const filtered = originalGuestList.filter((row) =>
      (row.name || '')?.toLowerCase()?.includes(text?.toLowerCase()),
    );
    onsetPage(0);
    setGuestList(filtered);
  };

  const filteredRecords = (_, field) => {
    setSearchText('');
    onsetPage(0);

    if (field === 'all') {
      setGuestList(originalGuestList);
      resetFilterDataList();
      isFilterApplied.current = false;
      return;
    }

    isFilterApplied.current = true;

    const activeFilters = {};
    filterDataList.forEach((filter) => {
      const selectedValues = filter.subMenu.filter((s) => s.selected).map((s) => s.id);
      if (selectedValues.length > 0) activeFilters[filter.field] = selectedValues;
    });

    const filtered = originalGuestList.filter((row) => {
      for (let [f, values] of Object.entries(activeFilters)) {
        if (!values?.includes(row[f])) return false;
      }
      return true;
    });

    setGuestList(filtered);
  };
  const isTemplateValid = (row) => {
    if (row?.htmlTemplateBody) {
      return row.htmlTemplateBody.trim().length > 0;
    }
    const emailTemplate = row?.emailTemplate;
    if (!emailTemplate) return false;
    if (!emailTemplate.header?.image || !emailTemplate.footer?.image?.image) return false;
    if (!Array.isArray(emailTemplate.body) || emailTemplate.body.length === 0) return false;

    return emailTemplate.body.some((body) => {
      const hasText =
        Array.isArray(body.text) && body.text.length > 0 && body.text.every((t) => !!t.text);
      const hasImageOk = Array.isArray(body.image) ? body.image.every((im) => !!im.image) : true;
      const hasButtonOk = Array.isArray(body.button)
        ? body.button.every((b) => !!b.url && !!b.text)
        : true;
      return hasText && hasImageOk && hasButtonOk;
    });
  };

  const handlePublishPopupOpen = () => {
    const row = selectedValue.group;
    const valid = isTemplateValid(row);

    if (!valid && !row?.isPublished) {
      setSelectedValue((prev) => ({
        ...prev,
        title: '',
        description:
          'The selected email template cannot be marked for publish as its details are yet to be filled.',
        showTemplateWarning: true,
        anchorEl: null,
      }));
      return;
    }

    setSelectedValue((prev) => ({
      ...prev,
      title: row.isPublished
        ? 'Are you sure you want to Unpublish'
        : 'Are you sure you want to Publish',
      publishConfirm: true,
      publishButtonLabel: row.isPublished ? 'Unpublish' : 'Publish',
      anchorEl: null,
    }));
  };
  const onClickDuplicate = (e) => {
    e.stopPropagation();
    setSelectedValue((prev) => ({
      ...prev,
      anchorEl: null,
      duplicateStatus: true,
      title: 'Are you sure you want to duplicate',
      description: 'Please provide a name to be assigned for the duplicated page.',
    }));
  };

  const markForPublish = async () => {
    try {
      dispatch(stepUpLoaderCount());
      if (!selectedValue.group.isPublished) {
        await publishEmailTemplate(selectedValue.id, selectedHotel, lang);
        notificationManager(success, 'Email Service published successfully');
      } else {
        await unPublishEmailTemplate(selectedValue.id, selectedHotel, lang);
        notificationManager(success, 'Email Service unpublished successfully');
      }
      await dispatch(fetchAllEmailServices(selectedHotel));
    } catch (e) {
      notificationManager(
        danger,
        e?.graphQLErrors?.[0]?.message || 'Email Service publish toggle failed',
      );
    } finally {
      dispatch(stepDownLoaderCount());
      handleCloseMenu();
    }
  };

  const onWarningOk = () => {
    onViewToggle(GUEST_VIEW, true, selectedValue.group);
  };

  const handleDeletePopupOpen = () => {
    setSelectedValue((prev) => ({
      ...prev,
      title: 'Are you sure you want to delete',
      description: 'This action cannot be reversed.',
      status: true,
      anchorEl: null,
    }));
  };

  const handlePublishedDeleteOpen = () => {
    setSelectedValue((prev) => ({
      ...prev,
      title: '',
      name: prev?.name || '',
      description:
        'The selected page cannot be deleted as it is already published to the Guest Mobile App',
      status: false,
      anchorEl: null,
      showDeleteWarning: true,
      type: 'delete',
    }));
  };
  const onDeleteCancel = () => {
    handleCloseMenu();
  };

  const deleteSingle = async () => {
    if (selectedValue?.group?.isPublished) {
      setSelectedValue((prev) => ({
        ...prev,
        title: '',
        description:
          'The selected page cannot be deleted as it is already published to the Guest Mobile App',
        status: false,
        showDeleteWarning: true,
      }));
      return;
    }
    try {
      dispatch(stepUpLoaderCount());
      await deleteEmailTemplate(selectedValue.id, selectedHotel, lang);
      await dispatch(fetchAllEmailServices(selectedHotel));
      notificationManager(success, 'Email Service deleted successfully');
    } catch (e) {
      notificationManager(
        danger,
        e?.graphQLErrors?.[0]?.message || 'Email Service deletion failed',
      );
    } finally {
      dispatch(stepDownLoaderCount());
      handleCloseMenu();
    }
  };

  const onConfirmDuplicate = async (e, name) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    try {
      dispatch(stepUpLoaderCount());
      const currentItem = selectedValue.group;
      const duplicatePayload = {
        hotelId: selectedHotel,
        name: name,
        fromName: currentItem.fromName || 'Default Sender',
        fromAddress: currentItem.fromAddress || 'noreply@example.com', // Provide default if missing
        subject: currentItem.subject || 'Email Service',
        htmlTemplateBody: currentItem.htmlTemplateBody || '',
        cc: currentItem.cc || '',
        bcc: currentItem.bcc || '',
        templateType: currentItem.templateType || 'GENERAL',
        audience: {
          guests: currentItem.audience?.guests || {
            blockCodes: { logic: 'AND', values: [] },
            loyaltyTiers: { logic: 'AND', values: [] },
            roomTypes: { logic: 'AND', values: [] },
            rooms: { logic: 'AND', values: [] },
          },
          staff: currentItem.audience?.staff || { emails: [] },
          type: currentItem.audience?.type || 'GUESTS',
        },
      };
      await createEmailServiceTemplate(duplicatePayload, lang);
      notificationManager(success, 'Email service duplicated successfully');
      await dispatch(fetchAllEmailServices(selectedHotel));
    } catch (error) {
      notificationManager(
        danger,
        error?.graphQLErrors?.[0]?.message || 'Email service duplication failed',
      );
    } finally {
      dispatch(stepDownLoaderCount());
      handleCloseMenu();
    }
  };

  return (
    <div>
      {/* Single-row popups */}
      {selectedValue.id && selectedValue.publishConfirm && (
        <CustomPopupModel
          popUp={{
            ...selectedValue,
            status: selectedValue.publishConfirm,
            buttonName: selectedValue.publishButtonLabel,
          }}
          onClick={markForPublish}
          onClickCancel={handleCloseMenu}
        />
      )}

      {selectedValue.id && selectedValue.status && (
        <DeletePopupModel
          deletePopUp={selectedValue}
          onDelete={deleteSingle}
          onDeleteCancel={onDeleteCancel}
        />
      )}

      {selectedValue.duplicateStatus && selectedValue.id && (
        <DuplicatePopupModel
          duplicatePopUp={selectedValue}
          onDuplicate={onConfirmDuplicate}
          onDuplicateCancel={handleCloseMenu}
        />
      )}
      {selectedValue.showDeleteWarning && selectedValue.id && (
        <WarningPopup
          warningPopup={{
            name: selectedValue.name,
            description:
              'The selected page cannot be deleted as it is already published to the Guest Mobile App',
            status: true,
            type: 'delete',
          }}
          onWarningClose={handleCloseMenu}
        />
      )}
      {selectedValue.id && selectedValue.showTemplateWarning && (
        <WarningPopup
          warningPopup={{
            ...selectedValue,
            title: '',
            status: true,
          }}
          onWarningClose={handleCloseMenu}
          isClose={false}
          onWarningOk={onWarningOk}
          close='Edit Template'
        />
      )}

      <div className='mt-4 mb-4 group-list-container'>
        <SearchBar
          type='CREATE'
          buttonName='Add Template'
          setSearchText={handleSearchText}
          onOpenDrawer={onOpenDrawer}
          filterList={filterDataList}
          setFilterDataList={setFilterDataList}
          filteredRecoard={filteredRecords}
          displayAddOrEdit='show'
          visible={visible}
          setVisible={setVisible}
          popUp={popUp}
          setPopUp={setPopUp}
          placeHolder={guestList}
          setPlaceHolder={setGuestList}
          globalPlaceHolder={originalGuestList}
          dispatch={dispatch}
          displayText='Email services'
          module='EMAIL_SERVICE'
          searchText={searchText}
          setSearch={setSearchText}
          actionMenuItems={[]}
          handleAction={() => {}}
          mappingApi={createDataSortMapping}
          deviceFilterCount={deviceFilterCount}
          statusFilterCount={statusFilterCount}
          audienceFilterCount={audienceFilterCount}
          setGlobalPlaceHolder={setEmailServices}
          setDrag={setDragEnabled}
        />

        <div ref={wrapperRef} className='row mx-2 d-flex mt-4'>
          {loading ? (
            <div className='pt-4 col-md-12'>
              <LinearProgressBar />
            </div>
          ) : (
            <>
              <TableContainer className='guest-list-table'>
                <Table className='list-table' aria-label='guest email services table'>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: '60px', minWidth: '60px', maxWidth: '60px' }} />
                      <TableCell style={{ width: '35%' }}>Name</TableCell>
                      <TableCell style={{ width: '20%' }} align='center'>
                        Audience
                      </TableCell>
                      <TableCell style={{ width: '20%' }} align='center'>
                        Status
                      </TableCell>
                      <TableCell style={{ width: '25%' }} align='right'></TableCell>
                    </TableRow>
                  </TableHead>

                  <DragDropContext
                    onDragEnd={(event) =>
                      handleOnDragEnd(
                        event,
                        setVisible,
                        guestList,
                        setGuestList,
                        false,
                        page,
                        rowsPerPage,
                      )
                    }
                    isDropDisabled={dragDisabled}
                  >
                    <Droppable droppableId='guests' isDropDisabled={dragDisabled}>
                      {(provided) => (
                        <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                          {(rowsPerPage > 0
                            ? guestList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            : guestList
                          ).map((item, i) => (
                            <Draggable
                              key={item.id}
                              draggableId={item.id}
                              index={i}
                              isDragDisabled={dragDisabled}
                            >
                              {(prov) => (
                                <TableRow
                                  ref={prov.innerRef}
                                  {...prov.draggableProps}
                                  {...prov.dragHandleProps}
                                  className='border-white pointer'
                                  onClick={(e) => onViewDetails(item, e)}
                                >
                                  <TableCell
                                    style={{ width: '60px', minWidth: '60px', maxWidth: '60px' }}
                                  >
                                    {!dragDisabled && (
                                      <DragIndicator className='res-menu-accordion-drag' />
                                    )}
                                  </TableCell>

                                  <TableCell style={{ width: '35%' }}>{item?.name}</TableCell>

                                  <TableCell style={{ width: '20%' }} align='center'>
                                    {getAudienceLabel(item)}
                                  </TableCell>

                                  <TableCell style={{ width: '20%' }} align='center'>
                                    <div className='d-flex justify-content-center align-items-center'>
                                      <div
                                        className={`${item.statusClass} pill d-flex justify-content-center align-items-center w-35 py-2 px-0`}
                                      >
                                        <span>{item.status}</span>
                                      </div>
                                    </div>
                                  </TableCell>

                                  <TableCell
                                    align='right'
                                    style={{ width: '25%' }}
                                    className='kebab-menu'
                                  >
                                    {permissions[RBAC_TYPE_EMAIL_SERVICES] ===
                                      RBAC_PERMISSION_LEVEL_FULL_ACCESS && (
                                      <Button
                                        aria-controls='guest-row-menu'
                                        aria-haspopup='true'
                                        onClick={(e) => handleCellMenuClick(e, item)}
                                        className='showpopupInner'
                                      >
                                        <MoreHoriz className='showpopupInner' />
                                      </Button>
                                    )}

                                    <Menu
                                      anchorEl={selectedValue?.anchorEl}
                                      disableRestoreFocus
                                      MenuListProps={{ autoFocusItem: false }}
                                      keepMounted
                                      open={
                                        Boolean(selectedValue.anchorEl) &&
                                        selectedValue.id === item.id
                                      }
                                      onClose={handleCloseMenu}
                                      className='shadow'
                                    >
                                      {item.isPublished ? (
                                        <>
                                          <MenuItem
                                            className='pe-4'
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handlePublishPopupOpen();
                                            }}
                                          >
                                            <RemoveCircleIcon className='me-2 action-menu-icon' />
                                            Unpublish
                                          </MenuItem>
                                          <MenuItem
                                            className='pe-4'
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openViewDrawer(selectedValue.group, e);
                                            }}
                                          >
                                            <VisibilityIcon className='me-2 action-menu-icon' />
                                            View
                                          </MenuItem>
                                          <MenuItem
                                            className='pe-4'
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onClickDuplicate(e);
                                            }}
                                          >
                                            <ContentCopyIcon className='me-2 action-menu-icon' />
                                            Duplicate
                                          </MenuItem>
                                          <MenuItem
                                            className='pe-4'
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handlePublishedDeleteOpen();
                                            }}
                                          >
                                            <DeleteForeverIcon className='me-2 action-menu-icon' />
                                            Delete
                                          </MenuItem>
                                        </>
                                      ) : (
                                        <>
                                          <MenuItem
                                            className='pe-4'
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handlePublishPopupOpen();
                                            }}
                                          >
                                            <CheckBoxIcon className='me-2 action-menu-icon' />
                                            Publish
                                          </MenuItem>

                                          <MenuItem
                                            className='pe-4'
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openViewDrawer(selectedValue.group, e);
                                            }}
                                          >
                                            <VisibilityIcon className='me-2 action-menu-icon' />
                                            View
                                          </MenuItem>
                                          <MenuItem
                                            className='pe-4'
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onClickDuplicate(e);
                                            }}
                                          >
                                            <ContentCopyIcon className='me-2 action-menu-icon' />
                                            Duplicate
                                          </MenuItem>
                                          <MenuItem
                                            className='pe-4'
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeletePopupOpen();
                                            }}
                                          >
                                            <DeleteForeverIcon className='me-2 action-menu-icon' />
                                            Delete
                                          </MenuItem>
                                        </>
                                      )}
                                    </Menu>
                                  </TableCell>
                                </TableRow>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </TableBody>
                      )}
                    </Droppable>
                  </DragDropContext>

                  <TableFooter>
                    <TableRow className='paginator'>
                      <TablePagination
                        rowsPerPageOptions={[10, 20, 30, { label: 'All', value: -1 }]}
                        count={guestList.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        SelectProps={{
                          inputProps: { 'aria-label': 'rows per page' },
                          native: true,
                        }}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        ActionsComponent={TablePaginationActions}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestList;
